/**
 * reading a request's projects out of folder state.
 */
import {
  pickCaseContentTemplate,
  pickProjectsContentTemplate,
} from '@redact-modules/casemedia/contentTemplates';
import type { FolderStateType } from '@redact-modules/casemedia/store';

import { parseProjectPointers, ProjectPointer } from './models';
import { namespace as projectsNamespace, ProjectsSlice } from './store';
import type { CombinedAssetSummary } from './combineServices';
import type { ProjectBatesStamp } from './batesStamp';
import { UNREAD, type ProjectRedactionRecord } from './projectRedactionAsset';
import type { TDOId } from '@common-modules/universal/models/Brands';
import {
  DEFAULT_MERGED_FILENAME_OPTIONS,
  type MergedFilenameOptions,
} from './models';

const NO_PROJECTS: readonly ProjectPointer[] = [];

/** Every project recorded on this request, in stored order. */
export const projectsOfFolder = (
  folder: FolderStateType | null | undefined
): readonly ProjectPointer[] => {
  const template = pickProjectsContentTemplate(folder?.contentTemplates);
  if (!template) {
    return NO_PROJECTS;
  }
  const parsed = parseProjectPointers(template.sdo?.data);
  // Preserve the shared-empty reference so a request with no projects does not
  // hand consumers a fresh [] on every call — the same rule
  // `selectEditorRequestDocuments` follows.
  return parsed.length ? parsed : NO_PROJECTS;
};

/**
 * The request's single project, or null.
 */
export const projectOfFolder = (
  folder: FolderStateType | null | undefined
): ProjectPointer | null => projectsOfFolder(folder)[0] ?? null;

/**
 * The SDO id that `writeProjectPointers` must upsert.
 */
export const projectsSdoIdOfFolder = (
  folder: FolderStateType | null | undefined
): string | undefined =>
  pickProjectsContentTemplate(folder?.contentTemplates)?.sdoId ??
  pickCaseContentTemplate(folder?.contentTemplates)?.sdoId;

/**
 * The cached membership of a project, or undefined when none has been read.
 */
export const selectProjectMembers =
  (projectTdoId: TDOId | undefined) =>
  (state: ProjectsSlice): readonly TDOId[] | undefined =>
    projectTdoId
      ? state?.[projectsNamespace]?.membersByProject?.[projectTdoId]
      : undefined;

/**
 * When a project's membership was last decided, or undefined if it has not
 * been read. See `ProjectsState.membershipDecidedAt`.
 */
export const selectMembershipDecidedAt =
  (projectTdoId: TDOId | undefined) =>
  (state: ProjectsSlice): string | undefined =>
    projectTdoId
      ? state?.[projectsNamespace]?.membershipDecidedAt?.[projectTdoId]
      : undefined;

/** Is a project's membership being read right now? */
export const selectProjectLoading =
  (projectTdoId: TDOId | undefined) =>
  (state: ProjectsSlice): boolean =>
    projectTdoId
      ? !!state?.[projectsNamespace]?.loading?.[projectTdoId]
      : false;

/**
 * Is a request's project TDO being minted right now? Keyed on the CASE, since
 * the whole point of the state is that there is not a project yet.
 */
export const selectProjectCreating =
  (caseId: string | undefined) =>
  (state: ProjectsSlice): boolean =>
    caseId ? !!state?.[projectsNamespace]?.creating?.[caseId] : false;

/** Is a membership change being written right now? */
export const selectProjectSaving =
  (projectTdoId: TDOId | undefined) =>
  (state: ProjectsSlice): boolean =>
    projectTdoId ? !!state?.[projectsNamespace]?.saving?.[projectTdoId] : false;

/**
 * The version the rebuild in flight was issued, or undefined.
 *
 * Undefined between the press and the server settling it, and again once it
 * finishes. A caller showing "building version N" falls back to its own guess
 * only in that first window.
 */
export const selectCombiningVersion =
  (projectTdoId: TDOId | undefined) =>
  (state: ProjectsSlice): number | undefined =>
    projectTdoId
      ? state?.[projectsNamespace]?.combiningVersion?.[projectTdoId]
      : undefined;

/** Is this project's merged document being built right now? */
export const selectProjectCombining =
  (projectTdoId: TDOId | undefined) =>
  (state: ProjectsSlice): boolean =>
    projectTdoId
      ? !!state?.[projectsNamespace]?.combining?.[projectTdoId]
      : false;

/**
 * The project's newest merged document.
 */
export const selectCombinedAsset =
  (projectTdoId: TDOId | undefined) =>
  (state: ProjectsSlice): CombinedAssetSummary | null | undefined =>
    projectTdoId
      ? state?.[projectsNamespace]?.combinedByProject?.[projectTdoId]
      : undefined;

/**
 * How this project's merged filename is assembled.
 */
export const selectMergedFilenameOptions =
  (projectTdoId: TDOId | undefined) =>
  (state: ProjectsSlice): MergedFilenameOptions =>
    selectStoredMergedFilenameOptions(projectTdoId)(state) ??
    DEFAULT_MERGED_FILENAME_OPTIONS;

/**
 * The STORED options, or undefined when none have been read back yet.
 */
export const selectStoredMergedFilenameOptions =
  (projectTdoId: TDOId | undefined) =>
  (state: ProjectsSlice): MergedFilenameOptions | undefined =>
    projectTdoId
      ? state?.[projectsNamespace]?.filenameOptionsByProject?.[projectTdoId]
      : undefined;

/**
 * The project's Bates settings, or null when it carries none.
 *
 * Undefined means the project has NOT been looked up — a third answer the
 * caller must not read as "no stamping", the same distinction
 * `combinedByProject` draws.
 */
export const selectProjectBatesStamp =
  (projectTdoId: TDOId | undefined) =>
  (state: ProjectsSlice): ProjectBatesStamp | null | undefined =>
    projectTdoId
      ? state?.[projectsNamespace]?.batesByProject?.[projectTdoId]
      : undefined;

/**
 * The merged document's redaction record (VE-30104), never undefined.
 *
 * An absent key means nobody has asked, which is `unread` — a state the
 * caller has to handle by name. That is the whole point of the union: the
 * Bates selector above returns `undefined` for the same situation and five
 * readers quietly treated it as "carries none".
 */
export const selectProjectRedaction =
  (projectTdoId: TDOId | undefined) =>
  (state: ProjectsSlice): ProjectRedactionRecord =>
    (projectTdoId
      ? state?.[projectsNamespace]?.redactionByProject?.[projectTdoId]
      : undefined) ?? UNREAD;

/** Is a write to the merged document's record in flight? */
export const selectProjectRedactionSaving =
  (projectTdoId: TDOId | undefined) =>
  (state: ProjectsSlice): boolean =>
    !!projectTdoId &&
    state?.[projectsNamespace]?.redactionWrite?.[projectTdoId]?.status ===
      'saving';

/** Did the last watermark write to this project fail? */
export const selectProjectRedactionWriteFailed =
  (projectTdoId: TDOId | undefined) =>
  (state: ProjectsSlice): boolean =>
    !!projectTdoId &&
    state?.[projectsNamespace]?.redactionWrite?.[projectTdoId]?.status ===
      'failed';

/**
 * Did the merged-document lookup for this project FAIL?
 */
export const selectCombinedLoadFailed =
  (projectTdoId: TDOId | undefined) =>
  (state: ProjectsSlice): boolean =>
    projectTdoId
      ? !!state?.[projectsNamespace]?.combinedLoadFailed?.[projectTdoId]
      : false;

/**
 * The merged document's display name for a project — the project TDO's own
 * `name`. Undefined until the project has been looked up; callers fall back
 * to the request's name, which is what a new project is named after.
 */
export const selectProjectName =
  (projectTdoId: TDOId | string | undefined) =>
  (state: ProjectsSlice): string | undefined =>
    projectTdoId
      ? state?.[projectsNamespace]?.nameByProject?.[String(projectTdoId)]
      : undefined;

/**
 * The name a rename in flight OVERWROTE, or undefined when none is.
 *
 * Read from the rollback slot rather than from `nameByProject`, which is the
 * whole point: redux-saga runs reducers before putting an action on its
 * channel, so by the time `handleRenameProject` runs, `nameByProject` already
 * holds the NEW name. The reducer stashes the pre-write value here, and this
 * is how the audit trail can say what the document used to be called
 * (VE-31977).
 */
export const selectRenamedFrom =
  (projectTdoId: TDOId | string | undefined) =>
  (state: ProjectsSlice): string | undefined =>
    projectTdoId
      ? state?.[projectsNamespace]?.renameRollback?.[String(projectTdoId)]?.name
      : undefined;

/**
 * The project whose combined document is being PREVIEWED, or null.
 */
export const selectPreviewingCombinedFor = (
  state: ProjectsSlice
): string | null => state?.[projectsNamespace]?.previewingCombinedFor ?? null;
